import { defineStore } from 'pinia'
import { ref, computed, watch, markRaw } from 'vue'
import { useColorStore } from './colorStore'
import { generatePreviewImage } from '../utils/colorUtils'

export interface PreviewOptions {
  pixelSize: number
  algorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000'
  dithering?: 'none' | 'floyd-steinberg'
  strength?: number
  gamma?: number
  brightness?: number
  constraints?: {
    clampHighlights?: number
    liftShadows?: number
    saturationBoost?: number
  }
  posterizeLevels?: number
  downscaleRatio?: number
  denoiseStrength?: number
  edgePreserveStrength?: number
  preQuantizeLevels?: number
  // 颜色归并设置
  mergeColor?: {
    mergeMode: 'rgb' | 'cielab'
    mergeThreshold: number
  }
  baseFabricColor?: { r: number; g: number; b: number; hex: string }
  // 新增：像素回调
  onOriginal?: (x: number, y: number, hex: string, rgb: { r: number; g: number; b: number }) => void;
  onMatch?: (x: number, y: number, match: { id: number; name: string; hex: string; r: number; g: number; b: number }) => void;
  // 新增：归并结果回调
  onMergeResult?: (result: { originalCount: number; mergedCount: number; reduction: number }) => void;
}

export const usePreviewStore = defineStore('preview', () => {
  // 状态 - 分离两种渲染的状态
  const isRendering = ref(false)
  const renderProgress = ref(0)
  const renderMessage = ref('')
  const originalPreview = ref<string>('')
  const customPreview = ref<string>('')
  
  // 单独的状态用于相近色预览图渲染
  const isCustomRendering = ref(false)
  const customRenderProgress = ref(0)
  const customRenderMessage = ref('')
  
  // 颜色归并结果状态
  const mergeResult = ref<{ originalCount: number; mergedCount: number; reduction: number } | null>(null)
  
  // 获取颜色store
  const colorStore = useColorStore()
  
  // 计算属性
  const canRender = computed(() => {
    return colorStore.extractedColors && 
           colorStore.extractedColors.pixels.length > 0 &&
           colorStore.extractedColors.pixels[0].length > 0
  })
  
  // 默认预览选项
  const defaultOptions: PreviewOptions = {
    pixelSize: 4,
    algorithm: 'lab',
    dithering: 'none',
    strength: 1,
    gamma: 1,
    brightness: 0,
    constraints: {
      clampHighlights: 0,
      liftShadows: 0,
      saturationBoost: 0
    },
    posterizeLevels: 0,
    downscaleRatio: 1,
    denoiseStrength: 0,
    edgePreserveStrength: 0,
    preQuantizeLevels: 0
  }
  
  // 当前预览选项
  const currentOptions = ref<PreviewOptions>({ ...defaultOptions })
  
  // 渲染原始预览图
  async function renderOriginalPreview(options: Partial<PreviewOptions> = {}) {
    if (!canRender.value) return
    
    const mergedOptions = { ...currentOptions.value, ...options }
    currentOptions.value = mergedOptions
    
    isRendering.value = true
    renderProgress.value = 0
    renderMessage.value = '生成原始预览图...'
    
    try {
      const result = await generatePreviewImage(
        colorStore.extractedColors!.pixels,
        mergedOptions.pixelSize,
        [], // 不使用自定义颜色
        mergedOptions.algorithm,
        {
          baseFabricColor: colorStore.baseFabricColor || undefined,
          dithering: mergedOptions.dithering,
          strength: mergedOptions.strength,
          gamma: mergedOptions.gamma,
          brightness: mergedOptions.brightness,
          constraints: mergedOptions.constraints,
          posterizeLevels: mergedOptions.posterizeLevels,
          downscaleRatio: mergedOptions.downscaleRatio,
          denoiseStrength: mergedOptions.denoiseStrength,
          edgePreserveStrength: mergedOptions.edgePreserveStrength,
          preQuantizeLevels: mergedOptions.preQuantizeLevels,
          onRenderedMatrix: (matrix) => {
            if (colorStore.extractedColors) {
              colorStore.extractedColors.pixels = matrix;
            }
          },
          onProgress: (progress: number) => {
            renderProgress.value = progress * 0.5 // 原始预览占50%
            renderMessage.value = `生成原始预览图... ${Math.round(progress * 100)}%`
          }
        }
      )
      
      originalPreview.value = result
      renderProgress.value = 0.5
      renderMessage.value = '原始预览图生成完成'
      
    } catch (error) {
      console.error('生成原始预览图失败:', error)
      renderMessage.value = '生成原始预览图失败'
    } finally {
      // 注意：这里不重置 isRendering，因为还要继续生成自定义预览图
    }
  }
  // 只渲染相近色预览图（用于画风、算法、参数调整等）
  async function renderCustomPreviewOnly(options: Partial<PreviewOptions> = {}) {
    if (!canRender.value) return
    
    isCustomRendering.value = true
    customRenderProgress.value = 0
    customRenderMessage.value = '生成相近色预览图...'
    
    try {
      // 创建新的选项对象，不修改 currentOptions，避免影响原始预览图
      const mergedOptions = { 
        ...defaultOptions, 
        ...currentOptions.value, 
        ...options
      }
      // if(colorStore.previewExtractedColors){
      //   colorStore.previewExtractedColors.pixels = JSON.parse(JSON.stringify(colorStore.extractedColors!.pixels));
      // }
      const result = await generatePreviewImage(
        colorStore.extractedColors!.pixels,
        mergedOptions.pixelSize,
        colorStore.customColors.map(color => ({ ...color, name: color.name || `颜色${color.id}` })),
        mergedOptions.algorithm,
        {
          mergeColor: {
            mergeMode: colorStore.mergeMode,
            mergeThreshold: colorStore.mergeThreshold
          },
          baseFabricColor: colorStore.baseFabricColor || undefined,
          dithering: mergedOptions.dithering,
          strength: mergedOptions.strength,
          gamma: mergedOptions.gamma,
          brightness: mergedOptions.brightness,
          constraints: mergedOptions.constraints,
          posterizeLevels: mergedOptions.posterizeLevels,
          downscaleRatio: mergedOptions.downscaleRatio,
          denoiseStrength: mergedOptions.denoiseStrength,
          edgePreserveStrength: mergedOptions.edgePreserveStrength,
          preQuantizeLevels: mergedOptions.preQuantizeLevels,
          onRenderedMatrix: (matrix) => {
            if (colorStore.previewExtractedColors) {
              // 替换整个对象以触发响应，且像素矩阵使用 markRaw 避免深层追踪
              const prev = colorStore.previewExtractedColors
              colorStore.previewExtractedColors = {
                width: prev!.width,
                height: prev!.height,
                pixels: markRaw(matrix)
              }
            }
          },
          onMergeResult: (result) => {
            mergeResult.value = result
          },
          onProgress: (progress: number) => {
            customRenderProgress.value = progress
            customRenderMessage.value = `生成相近色预览图... ${Math.round(progress * 100)}%`
          }
        }
      )
      
      customPreview.value = result
      customRenderProgress.value = 1
      customRenderMessage.value = '相近色预览图生成完成'
      
    } catch (error) {
      console.error('生成相近色预览图失败:', error)
      customRenderMessage.value = '生成相近色预览图失败'
    } finally {
      isCustomRendering.value = false
      customRenderProgress.value = 0
      customRenderMessage.value = ''
    }
  }
  // 渲染自定义颜色预览图
  // async function renderCustomPreview(options: Partial<PreviewOptions> = {}) {
  //   if (!canRender.value || !colorStore.hasCustomColors) return
    
  //   const mergedOptions = { ...currentOptions.value, ...options }
  //   currentOptions.value = mergedOptions
    
  //   isRendering.value = true
  //   renderProgress.value = 0.5 // 从50%开始
  //   renderMessage.value = '生成自定义颜色预览图...'
    
  //   try {
  //     const result = await generatePreviewImage(
  //       colorStore.previewExtractedColors!.pixels,
  //       mergedOptions.pixelSize,
  //       colorStore.customColors.map(color => ({ ...color, name: color.name || `颜色${color.id}` })),
  //       mergedOptions.algorithm,
  //       {
  //         dithering: mergedOptions.dithering,
  //         strength: mergedOptions.strength,
  //         gamma: mergedOptions.gamma,
  //         brightness: mergedOptions.brightness,
  //         constraints: mergedOptions.constraints,
  //         posterizeLevels: mergedOptions.posterizeLevels,
  //         downscaleRatio: mergedOptions.downscaleRatio,
  //         denoiseStrength: mergedOptions.denoiseStrength,
  //         edgePreserveStrength: mergedOptions.edgePreserveStrength,
  //         preQuantizeLevels: mergedOptions.preQuantizeLevels,
  //         onOriginal: mergedOptions.onOriginal,
  //         onMatch: mergedOptions.onMatch,
  //         onProgress: (progress: number) => {
  //           renderProgress.value = 0.5 + progress * 0.5 // 自定义预览占50%
  //           renderMessage.value = `生成自定义颜色预览图... ${Math.round(progress * 100)}%`
  //         }
  //       }
  //     )
      
  //     customPreview.value = result
  //     renderProgress.value = 1
  //     renderMessage.value = '预览图生成完成'
      
  //   } catch (error) {
  //     console.error('生成自定义颜色预览图失败:', error)
  //     renderMessage.value = '生成自定义颜色预览图失败'
  //   }
  //   // 注意：状态重置由 renderAllPreviews 统一处理
  // }
  
  // 渲染所有预览图（用于提取颜色、网格设置变化等）
  async function renderAllPreviews(options: Partial<PreviewOptions> = {}) {
    if (!canRender.value) return
    
    try {
      await renderOriginalPreview({...options})
      await renderCustomPreviewOnly({...options})
    } catch (error) {
      console.error('渲染预览图失败:', error)
      renderMessage.value = '渲染预览图失败'
    } finally {
      // 确保在所有预览图生成完成后重置状态
      isRendering.value = false
      renderProgress.value = 0
      renderMessage.value = ''
    }
  }
  
  // 更新预览选项并重新渲染
  async function updatePreviewOptions(options: Partial<PreviewOptions>) {
    await renderAllPreviews(options)
  }
  
  // 重置预览图
  function resetPreviews() {
    originalPreview.value = ''
    customPreview.value = ''
    isRendering.value = false
    renderProgress.value = 0
    renderMessage.value = ''
    // 重置归并设置到默认值
    colorStore.setMergeMode('rgb')
    colorStore.setMergeThreshold(2)
  }
  
  // 合并监听器，避免重复触发
  watch(
    () => [
      colorStore.previewPixelSize,
      colorStore.customColors,
      colorStore.colorMatchingAlgorithm
    ],
    async ([newPixelSize, newCustomColors, newAlgorithm], [oldPixelSize, oldCustomColors, oldAlgorithm]) => {
      if (!canRender.value) return
      
      // 如果只是像素大小变化，重新渲染所有预览图
      if (newPixelSize !== oldPixelSize) {
        await renderAllPreviews({
          pixelSize: newPixelSize as number,
          algorithm: newAlgorithm as 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000'
        })
      }
      // 如果自定义颜色或算法变化，只重新渲染相近色预览图
      else if (colorStore.hasCustomColors && (
        newCustomColors !== oldCustomColors || 
        newAlgorithm !== oldAlgorithm
      )) {
        await renderCustomPreviewOnly({
          pixelSize: newPixelSize as number,
          algorithm: newAlgorithm as 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000'
        })
      }
    },
    { deep: true }
  )
  
  return {
    // 状态
    isRendering,
    renderProgress,
    renderMessage,
    originalPreview,
    customPreview,
    currentOptions,
    
    // 相近色预览图专用状态
    isCustomRendering,
    customRenderProgress,
    customRenderMessage,
    
    // 颜色归并结果
    mergeResult,
    
    // 计算属性
    canRender,
    
    // 方法
    renderOriginalPreview,
    renderAllPreviews,
    renderCustomPreviewOnly,
    updatePreviewOptions,
    resetPreviews
  }
})
